import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { usersApi, type BulkRow } from '@/api/users';
import { coursesApi } from '@/api/courses';
import { csvToList } from './bulkCsv';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { useIsMobile } from '@/hooks/useIsMobile';
import { Select } from '@/components/ui/Select';
import { humanizeError } from '@/lib/errors';
import { useAuthStore } from '@/stores/auth';
import { IS_APP_BUILD } from '@/lib/appBrand';

/**
 * Массовое добавление учеников: марафон → список → срок → предпросмотр → отчёт.
 *
 * ⚠️ ПРЕДПРОСМОТР ОБЯЗАТЕЛЕН И ПРОПУСТИТЬ ЕГО НЕЛЬЗЯ. Массовая операция без
 * него — это способ разослать двести неправильных писем за секунду. Кнопка
 * подтверждения появляется только после того, как администратор увидел разбор.
 *
 * ⚠️ ТЕХ, КТО УЖЕ УЧИТСЯ, НЕ ТРОГАЕМ. В списке группы половина людей обычно уже
 * в системе; выдать им новый пароль значит оставить полшколы без входа. Их
 * только записываем на курс.
 */

export type BulkStudentsModalProps = {
  open: boolean;
  onClose: () => void;
  onDone?: () => void;
};

type Step = 'input' | 'preview' | 'result';

export function BulkStudentsModal({ open, onClose, onDone }: BulkStudentsModalProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isAdmin = useAuthStore((s) => s.user?.role) === 'admin';
  const [step, setStep] = useState<Step>('input');
  const [list, setList] = useState('');
  // ⚠️ ВЫБИРАЕТСЯ ТАРИФ, А НЕ КУРС (заказчик 05.09.2026: «тариф назначать, а не
  // курс»). Курс выводится из тарифа: у каждого тарифа он свой и обязателен —
  // тариф без курса нельзя ни создать, ни назначить.
  const [tariffId, setTariffId] = useState('');
  const [until, setUntil] = useState('');
  const [fileName, setFileName] = useState('');

  // Тарифы для выбора. Только действующие: назначить выключенный нельзя, а в
  // списке он путает — их там несколько штук с одинаковыми названиями.
  const tariffs = useQuery({
    queryKey: ['admin', 'tariffs', 'for-bulk'],
    queryFn: () => coursesApi.listTariffs(),
    // В приложении выбора тарифа нет (решение владельца 13.10.2026: ни слова «тариф») — загрузка идёт без него,
    // доступ выдаётся позже из карточки на сайте.
    enabled: open && !IS_APP_BUILD,
  });
  const marathons = useQuery({
    queryKey: ['admin', 'marathons', 'for-bulk'],
    queryFn: () => coursesApi.listAllMarathons({ limit: 100, template: 'false' }),
    enabled: open,
  });
  const marathonTitle = (id?: string | null) =>
    (marathons.data?.items ?? []).find((m) => m.id === id)?.title ?? '';

  const activeTariffs = (tariffs.data ?? []).filter((x) => x.is_active && x.marathon_id);
  const tariffOptions = [
    { value: '', label: t('bulkStudents.tariffNone') },
    ...activeTariffs.map((x) => ({
      // ⚠️ В подписи И тариф, И курс: названий вроде «PreUp prem» на платформе
      // несколько, и отличить их можно только по курсу.
      value: x.id,
      label: marathonTitle(x.marathon_id) ? `${x.title} · ${marathonTitle(x.marathon_id)}` : x.title,
    })),
  ];
  const chosenTariff = activeTariffs.find((x) => x.id === tariffId);

  const preview = useMutation({
    mutationFn: () => usersApi.bulkPreview(list),
    onSuccess: () => setStep('preview'),
  });

  /**
   * Отправка на сервер.
   *
   * 🔴 РАНЬШЕ ЗДЕСЬ БЫЛО ДВА ЗАПРОСА ПОДРЯД: завести, потом зачислить. На 348
   * людях это развалилось — браузер бросил ждать на 30-й секунде, строка с
   * заведением упала, и зачисление на тариф НЕ ВЫПОЛНИЛОСЬ ВОВСЕ: 348 человек
   * остались без доступа, а таблица с паролями не дошла.
   *
   * Теперь список и тариф уходят ОДНИМ запросом, сервер отвечает сразу и
   * доводит работу сам. Отчёт и пароли ждут в разделе «Импорты».
   */
  const create = useMutation({
    mutationFn: () =>
      usersApi.startImport({
        list,
        tariff_plan_id: chosenTariff?.id ?? null,
        marathon_id: chosenTariff?.marathon_id ?? null,
        tariff_title: chosenTariff?.title ?? '',
        // Без даты срок решает сервер по курсу: 90 дней, «один на один» — 365 (11.10.2026; до этого — срок
        // тарифа или 365).
        duration_days: until ? daysUntil(until) : undefined,
      }),
    onSuccess: () => {
      setStep('result');
      onDone?.();
    },
  });

  const close = () => {
    setStep('input');
    setList('');
    setTariffId('');
    setUntil('');
    setFileName('');
    preview.reset();
    create.reset();
    onClose();
  };

  const error = preview.error ?? create.error;

  // Кнопки шага: на телефоне — внизу окна во весь экран (правило 3), на компьютере — под содержимым, как было.
  // «Отмена» по своей ширине, главная кнопка — остальное: «Проверить список» и «Завести N» в половину 390 не влезали в строку.
  const row = isMobile ? 'grid grid-cols-[auto_1fr] gap-2' : 'flex justify-end gap-2 pt-2';
  const buttons =
    step === 'input' ? (
      <div className={row}>
        <Button type="button" variant="ghost" onClick={close}>
          {t('common.cancel')}
        </Button>
        <Button type="button" disabled={list.trim() === ''} loading={preview.isPending} onClick={() => preview.mutate()}>
          {t('bulkStudents.check')}
        </Button>
      </div>
    ) : step === 'preview' && preview.data ? (
      <div className={row}>
        <Button type="button" variant="ghost" onClick={() => setStep('input')}>
          {t('bulkStudents.back')}
        </Button>
        {/*
          Делать нечего, только если некого заводить И некого зачислять.
          Список, где все уже учатся, но выбран курс, — рабочий случай:
          группу переводят на новый марафон.
        */}
        <Button
          type="button"
          disabled={preview.data.new === 0 && !(tariffId && preview.data.existing > 0)}
          loading={create.isPending}
          onClick={() => create.mutate()}
        >
          {t('bulkStudents.confirm', { count: preview.data.new })}
        </Button>
      </div>
    ) : step === 'result' ? (
      <div className={row}>
        <Link
          to="/admin/imports"
          onClick={close}
          className="inline-flex items-center justify-center gap-2 rounded-pill bg-primary px-5 py-2.5 text-sm font-semibold text-on-primary"
        >
          <span className="material-symbols-outlined text-[18px]">list_alt</span>
          {t('bulkStudents.openImports')}
        </Link>
        <Button type="button" variant="ghost" onClick={close}>
          {t('common.done')}
        </Button>
      </div>
    ) : null;

  return (
    <Modal open={open} onClose={close} title={t('bulkStudents.title')} phoneFull footer={isMobile ? buttons : undefined}>
      {step === 'input' && (
        <div className="space-y-4">
          <p className="text-sm text-on-surface-variant">{t('bulkStudents.intro')}</p>

          <div>
            <label className="mb-1 block text-sm font-medium">{t('bulkStudents.listLabel')}</label>
            <textarea
              value={list}
              onChange={(e) => setList(e.target.value)}
              rows={8}
              spellCheck={false}
              placeholder={t('bulkStudents.placeholderExample')}
              className="w-full rounded-xl border border-outline-variant bg-surface p-3 font-mono text-sm"
            />
            <p className="mt-1 text-xs text-on-surface-variant">{t('bulkStudents.listHint')}</p>

            {/* ⚠️ ЗАГРУЗКА ФАЙЛОМ — ОСНОВНОЙ ПУТЬ ДЛЯ БОЛЬШИХ СПИСКОВ. Заказчик
                грузит по 400 человек: вставлять такое в поле руками неудобно, а
                из Excel приходит либо точка с запятой, либо табуляция. Файл
                разбирается на месте и попадает в то же поле — человек ВИДИТ, что
                именно разобралось, до того как нажать «Проверить». */}
            <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm font-semibold text-primary">
              <span className="material-symbols-outlined text-[18px]">upload_file</span>
              {t('bulkStudents.uploadCsv')}
              <input
                type="file"
                accept=".csv,.txt,text/csv,text/plain"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const parsed = csvToList(await file.text());
                  setList(parsed);
                  setFileName(file.name);
                  // Сбрасываем поле, иначе тот же файл повторно не выберется.
                  e.target.value = '';
                }}
              />
              {fileName && <span className="font-normal text-on-surface-variant">· {fileName}</span>}
            </label>

            {/* ⚠️ Публичного шаблона в сборке больше нет (техдолг п. 219: в нём
                лежали правдоподобные имена и почты, открытые любому). Шаблон
                отдаёт сервер на странице загрузки файлом — туда и ведём.
                Загрузка файлом только для admin: manager получил бы 403. */}
            {isAdmin && (
              <Link
                to="/admin/imports/new"
                onClick={close}
                // Было `-mt-2`: ссылка наезжала на «Загрузить таблицей (CSV)» над ней (кадр 390, 13.10.2026; на компьютере так же).
                className="mt-2 block text-xs font-semibold text-primary"
              >
                {t('bulkStudents.fileImportLink')}
              </Link>
            )}
          </div>

          {!IS_APP_BUILD && (
            <Select
              label={t('bulkStudents.tariffLabel')}
              options={tariffOptions}
              value={tariffId}
              onChange={(e) => setTariffId(e.target.value)}
            />
          )}
          {chosenTariff && (
            <p className="-mt-2 text-xs text-on-surface-variant">
              {t('bulkStudents.tariffHint', {
                course: marathonTitle(chosenTariff.marathon_id),
              })}
            </p>
          )}

          {/*
            Срок задаётся ДАТОЙ, а не числом дней. Группа учится вместе и
            заканчивает вместе; при отсчёте «на N дней» у ученика, заведённого
            неделей позже, доступ протянулся бы дольше остальных.
          */}
          {tariffId && (
            <div>
              <label className="mb-1 block text-sm font-medium">{t('bulkStudents.untilLabel')}</label>
              <input
                type="date"
                value={until}
                onChange={(e) => setUntil(e.target.value)}
                className="w-full rounded-xl border border-outline-variant bg-surface p-3 text-sm"
              />
              <p className="mt-1 text-xs text-on-surface-variant">{t('bulkStudents.untilHint')}</p>
            </div>
          )}

          {!isMobile && buttons}
        </div>
      )}

      {step === 'preview' && preview.data && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Counter label={t('bulkStudents.willCreate')} value={preview.data.new} accent />
            <Counter label={t('bulkStudents.alreadyStudying')} value={preview.data.existing} />
            <Counter label={t('bulkStudents.duplicates')} value={preview.data.duplicate} />
            <Counter label={t('bulkStudents.broken')} value={preview.data.invalid} />
          </div>

          <RowList rows={preview.data.rows} t={t} />

          <p className="text-xs text-on-surface-variant">{t('bulkStudents.existingUntouched')}</p>

          {!isMobile && buttons}
        </div>
      )}

      {/*
        ⚠️ ИТОГ ЗДЕСЬ БОЛЬШЕ НЕ ПОКАЗЫВАЕМ. Работа идёт на сервере и может занять
        минуту на четырёхстах людях — держать окно открытым всё это время незачем,
        а показывать «готово» раньше времени нельзя: именно так и вышло, что 348
        человек «завелись», а тарифа у них не оказалось.
        Отчёт и пароли ждут в разделе «Импорты».
      */}
      {step === 'result' && (
        <div className="space-y-4">
          <div className="rounded-card bg-primary/5 p-4">
            <p className="text-sm font-semibold text-on-surface">{t('bulkStudents.startedTitle')}</p>
            {!IS_APP_BUILD && <p className="mt-1 text-sm text-on-surface-variant">{t('bulkStudents.startedNote')}</p>}
          </div>
          {!isMobile && buttons}
        </div>
      )}

      {error && <p className="mt-3 text-sm text-error">{humanizeError(error)}</p>}
    </Modal>
  );
}

/**
 * Сколько дней осталось до выбранной даты.
 *
 * Готовое зачисление принимает число дней, а на экране человек выбирает дату —
 * перевод делаем здесь. Минимум один день: дата «сегодня» не должна означать
 * «доступа нет вовсе».
 */
function daysUntil(date: string): number {
  const end = new Date(date + 'T23:59:59');
  const days = Math.ceil((end.getTime() - Date.now()) / 86_400_000);
  return Math.max(1, days);
}

function Counter({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
  return (
    <div
      className={`rounded-xl border p-3 ${
        accent ? 'border-primary bg-primary/5' : 'border-outline-variant bg-surface-variant/30'
      }`}
    >
      <p className={`text-2xl font-bold tabular-nums ${accent ? 'text-primary' : ''}`}>{value}</p>
      <p className="text-xs text-on-surface-variant">{label}</p>
    </div>
  );
}

/** Разбор построчно. Показываем ВСЕ строки, а не только проблемные: увидеть
 *  «этих девятерых заведём, а этих троих нет» надо целиком. */
function RowList({ rows, t }: { rows: BulkRow[]; t: (k: string) => string }) {
  const tone: Record<BulkRow['state'], string> = {
    new: 'text-primary',
    existing: 'text-on-surface-variant',
    duplicate: 'text-on-surface-variant',
    invalid: 'text-error',
  };
  const label: Record<BulkRow['state'], string> = {
    new: t('bulkStudents.stateNew'),
    existing: t('bulkStudents.stateExisting'),
    duplicate: t('bulkStudents.stateDuplicate'),
    invalid: t('bulkStudents.stateInvalid'),
  };

  return (
    <div className="max-h-64 overflow-y-auto rounded-xl border border-outline-variant">
      <table className="w-full text-sm">
        <tbody>
          {rows.map((r) => (
            <tr key={r.line} className="border-b border-outline-variant/60 last:border-0">
              <td className="w-10 p-2 text-right text-xs tabular-nums text-on-surface-variant">
                {r.line}
              </td>
              <td className="p-2">
                <span className="font-medium">{r.full_name}</span>
                <span className="ml-2 break-all text-xs text-on-surface-variant">{r.email}</span>
              </td>
              <td className={`whitespace-nowrap p-2 text-right text-xs ${tone[r.state]}`}>
                {label[r.state]}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
